import React from "react";
import PageLayout from "../components/PageLayout";
import { useTranslation } from "../i18n/LanguageContext";

function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("privacyPage.title", "Privacy Policy")}
      subtitle={t("privacyPage.subtitle", "Last updated: July 2026")}
    >
      <div className="space-y-8 text-slate-300">
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.introductionTitle", "Introduction")}
          </h2>
          <p>
            {t(
              "privacyPage.introductionText",
              'PartyBuddy ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform.'
            )}
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.collectTitle", "Information We Collect")}
          </h2>
          <p className="mb-3">{t("privacyPage.collectIntro", "We collect information in the following ways:")}</p>
          <ul className="mb-4 space-y-2">
            {[
              ["account", "Account Information:", "Your name, email, phone number, profile picture, and preferences"],
              ["usage", "Usage Data:", "Information about how you interact with our platform"],
              ["location", "Location Data:", "Information about your location (with your consent)"],
              ["device", "Device Information:", "Information about the device you use to access PartyBuddy"],
            ].map(([key, fallbackLabel, fallbackText]) => (
              <li key={key} className="flex gap-3">
                <span className="text-blue-400">→</span>
                <span>
                  <strong>{t(`privacyPage.collect.${key}.label`, fallbackLabel)}</strong>{" "}
                  {t(`privacyPage.collect.${key}.text`, fallbackText)}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.useTitle", "How We Use Your Information")}
          </h2>
          <ul className="space-y-2">
            {[
              ["services", "To provide and maintain our services"],
              ["improve", "To improve user experience and develop new features"],
              ["updates", "To send you updates and promotional content (with consent)"],
              ["security", "To detect and prevent fraud or security issues"],
            ].map(([key, fallback]) => (
              <li key={key} className="flex gap-3">
                <span className="text-blue-400">→</span>
                <span>{t(`privacyPage.use.${key}`, fallback)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.securityTitle", "Data Security")}
          </h2>
          <p>
            {t(
              "privacyPage.securityText",
              "We implement appropriate security measures to protect your personal information. However, no method of transmission over the internet is entirely secure. We cannot guarantee absolute security but are committed to protecting your data."
            )}
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.rightsTitle", "Your Rights")}
          </h2>
          <p className="mb-3">{t("privacyPage.rightsIntro", "You have the right to:")}</p>
          <ul className="space-y-2">
            {[
              ["access", "Access your personal information"],
              ["correction", "Request correction of inaccurate data"],
              ["deletion", "Request deletion of your data"],
              ["marketing", "Opt-out of marketing communications"],
            ].map(([key, fallback]) => (
              <li key={key} className="flex gap-3">
                <span className="text-blue-400">→</span>
                <span>{t(`privacyPage.rights.${key}`, fallback)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-blue-300">
            {t("privacyPage.contactTitle", "Contact Us")}
          </h2>
          <p>
            {t("privacyPage.contactText", "If you have questions about this Privacy Policy, please contact us at")}{" "}
            <a
              href="mailto:privacy@partybuddy.app"
              className="text-blue-400 transition hover:text-blue-300"
            >
              privacy@partybuddy.app
            </a>
          </p>
        </section>
      </div>
    </PageLayout>
  );
}

export default PrivacyPolicy;
